import { useMemo } from "react";
import { useSelector } from "react-redux";
import { allUsersSelector } from "store/user/selector";
import { ProjectRowData, TransformedProjectData, AllUsersState, User } from "../types";

export const transformProjectData = (
  tableData: ProjectRowData[],
  allUsers: AllUsersState | null
): TransformedProjectData[] => {
  if (!allUsers) return [];

  return tableData?.map((project: ProjectRowData) => {
    const { managers: originalManagers, ...rest } = project;
    const clone = { ...rest, managers: "" } as TransformedProjectData;
    
    // Transform translation type based on options
    if (project.translation_type === "standard") {
      clone.translation_type = !!Number(project.include_expedited_turnaround)
        ? "expedited option"
        : project.translation_type;
    } else {
      clone.translation_type = !!Number(project.include_expedited_turnaround)
        ? "certified expedited option"
        : !!Number(project.include_notarization)
          ? "notarization"
          : !!Number(project.include_mail_hard_copy)
            ? "hardcopy mailing option"
            : project.translation_type;
    }

    // Transform managers array to comma-separated string
    const managerIds = (originalManagers || []).map(({ user_id }) => user_id);
    const allUsersList: User[] = [
      ...allUsers.projectManagers,
      ...allUsers.customers,
      ...allUsers.admins,
      ...allUsers.translators,
      ...allUsers.businessCustomers
    ];

    clone.managers = allUsersList
      .filter(({ user_id }) => managerIds.includes(user_id))
      .map(({ first_name, last_name }) => `${first_name} ${last_name}`)
      .join(",");

    return clone;
  });
};

export const getColumnWidths = () => ({
  order_name: 100,
  order_due_at: 90,
  order_status_name: 160,
  languages: 200,
  manager: 60,
  translator: 100,
  actionCol: 60,
});

export const isExpeditedOrder = (
  includeExpeditedTurnaround: string,
  orderStatusId: string
): boolean => {
  return (
    Number(includeExpeditedTurnaround) === 1 &&
    Number(orderStatusId) !== 6 &&
    Number(orderStatusId) !== 10
  );
};

export const useTransform = (tableData: any) => {
  const allUsers = useSelector(allUsersSelector);
  const transformedData = useMemo(
    () => {
      if (!allUsers) return [];
      return tableData?.map((it: any) => {
        const clone = { ...it };
        if (it.translation_type === "standard") {
          clone.translation_type = !!Number(it.include_expedited_turnaround)
            ? "expedited option"
            : it.translation_type;
        } else {
          clone.translation_type = !!Number(it.include_expedited_turnaround)
            ? "certified expedited option"
            : !!Number(it.include_notarization)
              ? "notarization"
              : !!Number(it.include_mail_hard_copy)
                ? "hardcopy mailing option"
                : it.translation_type;
        }
        const managers = (it.managers || []).map(
          ({ user_id }: { user_id: any }) => user_id
        );
        clone.managers = [
          ...allUsers?.projectManagers,
          ...allUsers?.customers,
          ...allUsers?.admins,
          ...allUsers?.translators,
          ...allUsers?.businessCustomers
        ]
          .filter(({ user_id }: { user_id: any }) => managers.includes(user_id))
          .map(
            ({
              first_name,
              last_name,
            }: {
              first_name: string;
              last_name: string;
            }) => `${first_name} ${last_name}`
          )
          .join(",");
        return clone;
      })},
    [tableData, allUsers]
  );
  return transformedData;
};
